import { DataSource, DataSourceId } from './data-source';

/**
 * A strongly-typed GeoDataset ID.
 */
export type GeoDatasetId = number;

export interface GeoDataset {
  id: GeoDatasetId;
  modelId: number;
  categoryId: number | null;
  iconId: number | null;
  /**
   * Gets the data view icon URI.
   */
  iconUri: string | null;
  name: string;
  description: string;
  properties?: GeoDatasetProperties;
  created: Date;
  updated: Date;
  sortOrder: number | null;
  geoDatasetExternalLayers?: GeoDatasetExternalLayer[] | null;
  geoDatasetInternalLayers?: GeoDatasetInternalLayer[] | null;
}

export interface GeoDatasetMap {
  geoDatasetExternalLayers?: GeoDatasetExternalLayer[] | null;
  geoDatasetInternalLayers?: GeoDatasetInternalLayer[] | null;
}

export interface GeoDatasetProperties {
  centerLat: number;
  centerLong: number;
  zoomFactor: number;
}

export type GeoDatasetPropertiesResult = {
  geoDatasetId: GeoDatasetId;
} & GeoDatasetProperties;

export interface GeoCoordinates {
  fromLatitudeColumn: string;
  fromLongitudeColumn: string;
  toLatitudeColumn: string;
  toLongitudeColumn: string;
  fillColor: string;
  sourceColor: string;
  targetColor: string;
  width: number;
  tooltip: string;
  path: string;
  contour: string;
}

/**
 * A strongly-typed GeoDataset external layer ID.
 */
export type GeoDatasetExternalLayerId = number;

export interface GeoDatasetExternalLayer {
  id: GeoDatasetExternalLayerId;
  geoDataSetId: GeoDatasetId;
  name: string;
  connectionId: DataSourceId;
  connection: DataSource;
  tableName?: string;
  layerOrder: number;
  dataLayerType: number;
  layerSettings: GeoDatasetExternalLayerSettings;
  properties?: GeoDatasetExternalLayerProperties[];
  featureProperties: GeoDatasetExternalLayerFeatureProperty[];
  geoFeatureProperties?: GeoDatasetExternalLayerGeoFeatureProperties[] | null;
  fileBlob: string | null;
  fileUri: string | null;
  minZoom?: number;
  maxZoom?: number;
  enabled: boolean;
}

export interface GeoDatasetExternalLayerSettings {
  color: string;
  width: number;
  opacity: number;
  filled: boolean;
  excludeUnusedFeatures: boolean;
  extruded: boolean;
}

export interface GeoDatasetExternalLayerGeoFeatureProperties {
  ID: number;
  Model: string;
  Scenario: string;
  [key: string]: string | number;
}

export interface GeoDatasetExternalLayerProperties {
  [key: string]: boolean;
}

export interface GeoDatasetInternalLayerProperties extends GeoDatasetExternalLayerProperties {}

export interface GeoDatasetExternalLayerFeatureProperty {
  featurePropertyName: string;
  configType: string;
  colourType: string;
  gradient: GeoDatasetExternalLayerFeatureGradient;
  discreteColours: GeoDatasetLayerDiscreteColour[];
  minColumn?: string;
  maxColumn?: string;
  tableJoinColumn: string;
  tableInfoColumn: string;
  active: boolean;
}

export interface GeoDatasetLayerDiscreteColour {
  colourId: number;
  legend: string;
  colour: string;
}

export type GeoDatasetExternalLayerFeatureGradient =
  | {
      isGradient: boolean;
      gradientType?: string | null;
      degrees: string | null;
      colors: GeoDatasetExternalFeatureGradientColour[];
    }
  | undefined;

export type LayerGeoJsonData = {
  type: string;
  features: {
    type: string;
    properties: Record<string, any>;
  }[];
};

export type RGBA = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export type GeoDatasetExternalFeatureGradientColour = {
  value: string;
  left: string;
};

export type GeoDatasetExternalLayerJsonData = {
  id: number;
  name: string;
  data: string;
};

export type BlobStorageItem = {
  blobName: string;
  blobUrl: string;
};

export type BlobItemContent = {
  name: string;
  content: string;
};

type PolyProperties = { tooltip: string; layerType: string };

export type PolyGeometry = [longitude: number, latitude: number];

export type Polyline = {
  name: string;
  colour: string;
  width: number;
  properties: PolyProperties;
  path: PolyGeometry[];
};

export type Polygon = {
  name: string;
  lineColour: string;
  fillColour: string;
  width: number;
  properties: PolyProperties;
  contour: PolyGeometry[];
};

/**
 * A strongly-typed GeoDataset internal layer ID.
 */
export type GeoDatasetInternalLayerId = number;

export interface GeoDatasetInternalLayer {
  id: GeoDatasetInternalLayerId;
  geoDataSetId: GeoDatasetId;
  name: string;
  connectionId: DataSourceId;
  connection: DataSource;
  tableName: string;
  dataLayerTypeId: number;
  properties?: GeoDatasetInternalLayerProperties[];
  layerSettings: GeoDatasetInternalLayerSettings;
  layerOrder: number;
  minZoom?: number;
  maxZoom?: number;
  geoCoordinates: GeoCoordinates[];
  enabled: boolean;
}

export type GeoDatasetInternalLayerSettings =
  | GeoDataseDataLayerLine
  | GeoDataseDataLayerArc
  | GeoDataseDataLayerCircle
  | GeoDatasetDataLayerPolyline
  | GeoDatasetDataLayerPolygon;

export interface GeoDatasetDataLayerType {
  id: GeoDatasetId;
  name: string;
}

export interface GeoDatasetDataLayerBase {
  colour: string;
  discreteColours: GeoDatasetLayerDiscreteColour[];
  width: string;
  category: string;
  visible: boolean;
}

export interface GeoDataseDataLayerCircle extends GeoDatasetDataLayerBase {
  fromLatitudeColumn: string;
  fromLongitudeColumn: string;
  filled: boolean;
  scaleZoom: boolean;
  radiusScaleFactor: number;
  fillColor: string;
}

export interface GeoDataseDataLayerArc extends GeoDatasetDataLayerBase {
  fromLatitudeColumn: string;
  fromLongitudeColumn: string;
  toLatitudeColumn: string;
  toLongitudeColumn: string;
  sourceColor: string;
  targetColor: string;
}

export interface GeoDataseDataLayerLine extends GeoDatasetDataLayerBase {
  fromLatitudeColumn: string;
  fromLongitudeColumn: string;
  toLatitudeColumn: string;
  toLongitudeColumn: string;
  sourceColor: string;
  targetColor: string;
  lineColor: string;
}

export interface GeoDatasetDataLayerPolyline extends GeoDatasetDataLayerBase {
  sourceColor: string;
  path: string;
}

export interface GeoDatasetDataLayerPolygon extends GeoDatasetDataLayerBase {
  fillColor: string;
  sourceColor: string;
  contour: string;
}

export interface GeoDatasetColour {
  applicationId: number;
  colourId: number;
  colour: string;
}

/**
 * Request payload used to upload a geo dataset external layer file.
 */
export type UploadGeoDatasetExternalLayerFilePayload = {
  file: File;
  fileName: string;
};

/**
 * Request payload used to create a geo dataset.
 */
export type CreateGeoDatasetPayload = Omit<GeoDataset, 'id' | 'iconUri' | 'created' | 'updated'>;

/**
 * Request payload used to update an existing data view.
 */
export type UpdateGeoDatasetPayload = CreateGeoDatasetPayload;

export type UseUpdateGeoDatasetPayload = UpdateGeoDatasetPayload & {
  geoDatasetId: GeoDatasetId;
};

export enum GeoDatasetInternalLayerType {
  Line = 1,
  Arc = 2,
  Circle = 3,
  Polyline = 4,
  Polygon = 5,
}

export enum GeoDatasetExternalLayerFeaturePropertyConfigType {
  AreaColour = 'Area Colour',
  LineThickness = 'Line Thickness',
  LineColour = 'Line Colour',
}

export enum GeoDatasetExternalLayerFeaturePropertyColourType {
  DiscreteColour = 'Discrete Colour',
  GradientColour = 'Gradient Colour',
}

export enum GeoDatasetLayerType {
  ExternalLayer = 'ExternalLayer',
  InternalLayer = 'InternalLayer',
}
